
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import * as React from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { signIn, useSession } from 'next-auth/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { I18n, useTranslation } from 'components/hooks/use-translation';
import ProgressBar from 'components/atomic/atoms/progress-bar';
import GoogleBtn from 'components/atomic/atoms/google-login';
import CardLayout from 'components/layouts/card-layout';
import Button from 'components/atomic/atoms/button';
import Input from 'components/atomic/atoms/input';
import Text from 'components/atomic/atoms/text';
import devvSdk from 'utils/devv-sdk';
import Routes from 'utils/routes';
const ButtonsRow = styled('div') `
    display: flex;
    column-gap: 1rem;
`;
const StyledLink = styled(Text) `
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;
const initialFormValues = { first_name: '', last_name: '', email: '', password: '' };
const SignupPage = () => {
    const [progress, setProgress] = React.useState(1);
    const [loading, setLoading] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { t }: I18n = useTranslation();
    const router = useRouter();
    const session = useSession();
    React.useEffect(() => {
        if (session.status === 'authenticated')
            router.push(Routes.Paths);
    }, [session, router]);
    const handleGoogleLogin = () => {
        signIn('google');
    };
    const signupSchema = React.useCallback(() => {
        if (progress === 1) {
            return Yup.object().shape({
                password: Yup.string().trim().required(t('common:errors.requiredField')),
                email: Yup.string()
                    .trim()
                    .email(t('common:errors.invalidEmail'))
                    .required(t('common:errors.requiredField')),
            });
        }
        if (progress === 2) {
            return Yup.object().shape({
                first_name: Yup.string().trim().required(t('common:errors.requiredField')),
                last_name: Yup.string().trim().required(t('common:errors.requiredField')),
            });
        }
    }, [progress, t]);
    const handleSubmit = (values: {
        first_name: string;
        last_name: string;
        email: string;
        password: string;
    }) => {
        if (progress === 1) {
            setProgress(2);
            return;
        }
        setLoading(true);
        devvSdk
            .createUser(values)
            .then(() => {
            setLoading(false);
            router.push(Routes.VerifyEmail(values.email));
        })
            .catch(() => {
            setLoading(false);
            // TODO: handle specific errors
            enqueueSnackbar(t('signup:errors.generic'), { variant: 'error' });
        });
    };
    const form = useFormik({
        initialValues: initialFormValues,
        onSubmit: handleSubmit,
        validationSchema: signupSchema(),
    });
    return (<CardLayout headerType="only-logo" title={t('signup:title')}>
            <Stack spacing="1rem">
                <ProgressBar variant="determinate" value={(100 * progress) / 2 - 5}/>
                <Text variant="h4" color="primary">
                    {t('signup:title')}
                </Text>
                <Text variant="subtitle1" style={{ marginBottom: '1rem' }}>
                    {t('signup:subtitle')}
                </Text>
                {progress === 1 && (<>
                        <GoogleBtn onClick={() => handleGoogleLogin()} text={t('signup:buttons.googleSignup')}/>
                        <Divider>
                            <Text color="text.secondary">{t('common:terms.or')}</Text>
                        </Divider>
                        <Input label={t('signup:inputs.email')} onChange={form.handleChange('email')} value={form.values.email} error={form.touched.email ? !!form.errors.email : undefined} helperText={form.touched.email ? form.errors.email : undefined}/>
                        <Input label={t('signup:inputs.password')} type="password" error={form.touched.password ? !!form.errors.password : undefined} value={form.values.password} onChange={form.handleChange('password')} helperText={form.touched.password ? form.errors.password : undefined}/>
                    </>)}
                {progress === 2 && (<>
                        <Input label={t('signup:inputs.name')} error={form.touched.first_name ? !!form.errors.first_name : undefined} onChange={form.handleChange('first_name')} value={form.values.first_name} helperText={form.touched.first_name ? form.errors.first_name : undefined}/>
                        <Input error={form.touched.last_name ? !!form.errors.last_name : undefined} label={t('signup:inputs.lastName')} onChange={form.handleChange('last_name')} value={form.values.last_name} helperText={form.touched.last_name ? form.errors.last_name : undefined}/>
                    </>)}
                {progress === 1 ? (<Button disabled={!form.isValid} fullWidth variant="contained" color="primary" endIcon={<ArrowForward />} onClick={() => form.handleSubmit()}>
                        {t('signup:buttons.continue')}
                    </Button>) : (<ButtonsRow>
                        <Button variant="outlined" color="primary" fullWidth onClick={() => setProgress(progress - 1)}>
                            {t('signup:buttons.back')}
                        </Button>
                        <Button disabled={!form.isValid} loading={loading} variant="contained" color="primary" fullWidth onClick={() => form.handleSubmit()}>
                            {t('signup:buttons.signup')}
                        </Button>
                    </ButtonsRow>)}
                {progress === 1 && (<Stack justifyContent="center" direction="row" sx={{ marginTop: '1rem' }}>
                        <Text align="center" sx={{ marginRight: 1 }}>
                            {t('signup:haveAnAccount')}
                        </Text>
                        <StyledLink fontWeight={600} color="primary">
                            <Link href={{
                pathname: Routes.SignIn,
                query: router.query,
            }}>
                                {t('signup:login')}
                            </Link>
                        </StyledLink>
                    </Stack>)}
            </Stack>
        </CardLayout>);
};
export default SignupPage;

    async function __Next_Translate__getStaticProps__18916ceb1cb__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/signup',
            loaderName: 'getStaticProps',
            loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__18916ceb1cb__ as getStaticProps }
  